import type {
    CommandResponse,
    ReclaimForAssetAlreadyInRentalCompanyCommand,
    ReclaimForAssetOnlyInRenterCompanyCommand,
    StartAccountRentalCommandTarget,
    StartExternalRentalCommandTarget,
    StartRentalCommand,
} from '../../domain/model/commands.types';
import {
    CreationFlows,
    type RentalCompany,
    type RenterPartnerCompany,
} from '../../domain/model/rental-companies.types';
import { RENTAL_TYPE, type Rental } from '../../domain/model/rentals.types';
import type { AccountRental, ExternalRental } from './generated/rental-api';
import * as api from './generated/rental-api';

export const mapStartRentalCommandToApi = (command: StartRentalCommand): api.StartRentalCommand => {
    if (command.target.type === RENTAL_TYPE.ACCOUNT) {
        return api.StartRentalCommand.parse({
            rental_company_asset_id: command.rentalCompanyAssetId,
            target: {
                type: command.target.type,
                renter_account_id: (command.target as StartAccountRentalCommandTarget).renterCompanyAccountId,
            },
        });
    }
    if (command.target.type === RENTAL_TYPE.EXTERNAL) {
        return api.StartRentalCommand.parse({
            rental_company_asset_id: command.rentalCompanyAssetId,
            target: {
                type: command.target.type,
                renter_identifier: (command.target as StartExternalRentalCommandTarget).renterIdentifier,
            },
        });
    }
    throw new Error('Invalid command target type');
};

export const mapStopRentalCommandToApi = (command: { rentalId: string }): api.StopRentalCommand =>
    api.StopRentalCommand.parse({
        rental_id: command.rentalId,
    });

export const mapSellVehicleCommandToApi = (command: { rentalId: string }): api.StopAndSellCommand =>
    api.StopAndSellCommand.parse({
        rental_id: command.rentalId,
    });

export const mapCommandApiResponseToDomain = (response: api.CommandResponse): CommandResponse => ({
    warnings: response.warnings,
});

const isAssetAlreadyInRentalCompanyTargetType = (
    command: ReclaimForAssetAlreadyInRentalCompanyCommand | ReclaimForAssetOnlyInRenterCompanyCommand
): command is ReclaimForAssetAlreadyInRentalCompanyCommand =>
    (command as ReclaimForAssetAlreadyInRentalCompanyCommand).rentalCompanyAssetId !== undefined;

const isAssetOnlyInRenterCompanyTargetType = (
    command: ReclaimForAssetAlreadyInRentalCompanyCommand | ReclaimForAssetOnlyInRenterCompanyCommand
): command is ReclaimForAssetOnlyInRenterCompanyCommand =>
    (command as ReclaimForAssetOnlyInRenterCompanyCommand).vin !== undefined;

export const mapReclaimCommandToApi = (
    command: ReclaimForAssetAlreadyInRentalCompanyCommand | ReclaimForAssetOnlyInRenterCompanyCommand
): api.ReclaimCommand => {
    if (isAssetAlreadyInRentalCompanyTargetType(command)) {
        return api.ReclaimCommand.parse({
            target: {
                rental_company_asset_id: command.rentalCompanyAssetId,
                type: 'ASSET_ALREADY_IN_RENTAL_COMPANY',
            },
            renter_account_id: command.renterCompanyAccountId,
        });
    }
    if (isAssetOnlyInRenterCompanyTargetType(command)) {
        return api.ReclaimCommand.parse({
            target: {
                vin: command.vin,
                rental_company_account_id: command.rentalCompanyAccountId,
                type: 'ASSET_ONLY_IN_RENTER_COMPANY',
            },
            renter_account_id: command.renterCompanyAccountId,
        });
    }
    throw new Error('Invalid command type');
};

export const mapRentalCompanyToDomain = (response: api.RentalCompany): RentalCompany => ({
    accountId: response.id,
    contractType: mapContractTypeToDomain(response.contract_type),
});

const mapContractTypeToDomain = (contractType: api.ContractType): RentalCompany['contractType'] => {
    switch (contractType) {
        case 'ACCOUNT_WIDE':
            return 'ACCOUNT_WIDE';
        case 'ON_DEMAND':
            return 'ON_DEMAND';
        case 'INACTIVE':
            return 'INACTIVE';
        default:
            throw new Error(`Unexpected contract type: ${contractType}`);
    }
};

const mapStateTypeToDomain = (state: api.State): Rental['state'] => {
    switch (state) {
        case 'STARTED':
            return 'STARTED';
        case 'STOPPED':
            return 'STOPPED';
        case 'SOLD':
            return 'SOLD';
        default:
            throw new Error(`Unexpected state type: ${state}`);
    }
};

export const mapRentalToDomain = (rental: api.Rental): Rental => {
    if ((rental as AccountRental).type === RENTAL_TYPE.ACCOUNT) {
        return {
            id: rental.id,
            type: RENTAL_TYPE.ACCOUNT,
            state: mapStateTypeToDomain(rental.state),
            rentalCompanyAssetId: rental.rental_company_asset_id,
            rentalCompanyAccountId: rental.rental_company_account_id,
            renterAssetId: (rental as AccountRental).renter_asset_id,
            renterAccountId: (rental as AccountRental).renter_account_id,
            startedAt: rental.started_at,
        };
    }
    if ((rental as ExternalRental).type === RENTAL_TYPE.EXTERNAL) {
        return {
            id: rental.id,
            type: RENTAL_TYPE.EXTERNAL,
            state: mapStateTypeToDomain(rental.state),
            rentalCompanyAssetId: rental.rental_company_asset_id,
            rentalCompanyAccountId: rental.rental_company_account_id,
            renterIdentifier: (rental as ExternalRental).renter_identifier,
            startedAt: rental.started_at,
        };
    }
    throw new Error('Unknown rental type');
};

export const mapRenterPartnerCompanyToDomain = (response: api.RenterPartnerCompany): RenterPartnerCompany => ({
    accountId: response.id,
    name: response.name,
    creationFlow: mapCreationFlowToDomain(response.creation_flow),
});

const mapCreationFlowToDomain = (
    creationFlow: api.CreationFlow | null | undefined
): RenterPartnerCompany['creationFlow'] => {
    switch (creationFlow) {
        case 'INVITATION_FLOW':
            return CreationFlows.INVITATION_FLOW;
        case 'ACCOUNT_ID_FLOW':
            return CreationFlows.ACCOUNT_ID_FLOW;
        default:
            return CreationFlows.INVITATION_FLOW;
    }
};
